import React, { ChangeEventHandler, useEffect, useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, TextField, IconButton,
    Select,
    MenuItem,
    SelectChangeEvent,
    Autocomplete, Box,
    Grid2,
    Typography,
    FormControl,
    Menu,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import { Edit, Delete, Save } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Equipment } from '../types/equipmentTypes';
import { Employee, ShowModalProps } from '../types/EmployeeTypes';
import { Project } from '../types/projectTypes';
import { getEmployees } from '../api/employeeApi';
import { getProjects } from '../api/projectApi';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ConfirmBulkDeleteModal from './ConfirmBulkDeleteModal';
import ColumnFilterMenu from './ColumnFilterMenu';
import { CategoryEquipament } from '../types/categoryEquipaments';
import { getCategoryEquipment } from '../api/equipmentApi';

const ID_DESVINCULADO = 999;

interface EquipmentsTableProps {
    equipments: Equipment[];
    selectedProject: boolean;
    onEditEquipment: (Equipment: Equipment) => void;
    onDeleteEquipment: (equipmentId: number) => void;
    /** Eliminación múltiple. Si no se entrega, la selección múltiple no se muestra. */
    onDeleteMultipleEquipments?: (equipmentIds: number[]) => Promise<void> | void;
    onDownloadFormat: () => void;
    onImportEquipments: ChangeEventHandler<HTMLInputElement>;
    onExportEquipments: () => void;
    showModal: ShowModalProps;
    onCloseModal: () => void;
    reportDate?: string; // Propiedad para saber si es histórico
}

const EquipmentTable: React.FC<EquipmentsTableProps> = ({
    equipments,
    selectedProject,
    onEditEquipment,
    onDeleteEquipment,
    onDeleteMultipleEquipments,
    onDownloadFormat,
    onImportEquipments,
    onExportEquipments,
    showModal,
    onCloseModal,
    reportDate
}) => {
    const [editMode, setEditMode] = useState<number | null>(null);
    const [editableEquipment, setEditableEquipment] = useState<Equipment | null>(null);
    const [categoryEquipmentList, setCategoryEquipmentList] = useState<CategoryEquipament[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [equipmentId, setEquipmentId] = useState(0);
    const [equipmentName, setEquipmentName] = useState('');
    const [formErrors, setFormErrors] = useState({
        project: false,
        employee: false,
    });
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('');
    const [filteredEquipments, setFilteredEquipments] = useState(equipments);

    // Selección múltiple para eliminar. Solo en el mantenedor (no en modo histórico).
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    const allowMultiSelect = !reportDate && !!onDeleteMultipleEquipments;

    const [columnFilters, setColumnFilters] = useState({
        project: [] as string[],
        costCenter: [] as string[],
        internalCode: [] as string[],
        vehicleNumber: [] as string[],
        name: [] as string[],
        owner: [] as string[],
        employee: [] as string[],
        rentalCost: [] as string[],
        observations: [] as string[]
    });

    // Visibilidad de columnas (por defecto todas visibles). Permite mostrar/ocultar columnas.
    const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
        project: true,
        costCenter: true,
        internalCode: true,
        vehicleNumber: true,
        name: true,
        owner: true,
        employee: true,
        rentalCost: true,
        observations: true,
    });

    // Menú para elegir qué columnas mostrar/ocultar
    const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null);

    const toggleColumn = (id: string) => {
        setVisibleColumns(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Central de coste asociada al proyecto del equipo (asignación automática por central de coste)
    const getCostCenter = (equipment: Equipment) => equipment.project?.costCenter || '';

    const [filterMenu, setFilterMenu] = useState<{
        anchorEl: null | HTMLElement;
        columnKey: keyof typeof columnFilters | null;
    }>({ anchorEl: null, columnKey: null });

    const getProjectName = (equipment: Equipment) => {
        if (!equipment.project) return 'Mantenimiento / Faena';
        if (equipment.project.id === ID_DESVINCULADO) return 'Desvinculado';
        return equipment.project.name || '';
    };

    useEffect(() => {
        // --- ETAPA 1: FILTRO GLOBAL ---
        const globalSearch = filter.toLowerCase();
        const filteredByGlobal = equipments.filter(equipment => {
            if (!globalSearch) return true;
            const patenteMatch = equipment.vehicleNumber?.toLowerCase().includes(globalSearch);
            const equipoMatch = equipment.name?.toLowerCase().includes(globalSearch);

            const projectName = getProjectName(equipment).toLowerCase();

            const proyectoMatch = projectName.includes(globalSearch);
            return patenteMatch || equipoMatch || proyectoMatch;
        });

        // --- ETAPA 2: FILTRO POR COLUMNAS ---
        const filteredByColumns = filteredByGlobal.filter(equipment => {
            const projectDisplay = getProjectName(equipment);
            const costCenterDisplay = getCostCenter(equipment);
            const internalCodeDisplay = equipment.internalCode || '';
            const vehicleNumberDisplay = equipment.vehicleNumber || '';
            const nameDisplay = equipment.name || '';
            const ownerDisplay = Number(equipment.owner) === 1 ? 'Propio' : 'Rentado';
            const employeeDisplay = equipment.employee
                ? `${equipment.employee.name} ${equipment.employee.firstName} ${equipment.employee.lastName}`
                : 'No aplica';
            const rentalCostDisplay = new Intl.NumberFormat('es-CL', {
                style: 'currency',
                currency: 'CLP',
                minimumFractionDigits: 0,
            }).format(equipment.rentalCost || 0);
            const observationsDisplay = equipment.observations || '';

            return (
                (columnFilters.project.length === 0 || columnFilters.project.includes(projectDisplay)) &&
                (columnFilters.costCenter.length === 0 || columnFilters.costCenter.includes(costCenterDisplay)) &&
                (columnFilters.internalCode.length === 0 || columnFilters.internalCode.includes(internalCodeDisplay)) &&
                (columnFilters.vehicleNumber.length === 0 || columnFilters.vehicleNumber.includes(vehicleNumberDisplay)) &&
                (columnFilters.name.length === 0 || columnFilters.name.includes(nameDisplay)) &&
                (columnFilters.owner.length === 0 || columnFilters.owner.includes(ownerDisplay)) &&
                (columnFilters.employee.length === 0 || columnFilters.employee.includes(employeeDisplay)) &&
                (columnFilters.rentalCost.length === 0 || columnFilters.rentalCost.includes(rentalCostDisplay)) &&
                (columnFilters.observations.length === 0 || columnFilters.observations.includes(observationsDisplay))
            );
        });

        setFilteredEquipments(filteredByColumns);
    }, [filter, columnFilters, equipments]);

    // La selección se limita siempre a las filas visibles: si un filtro oculta una fila
    // seleccionada, se deselecciona para no eliminar registros que el usuario no está viendo.
    useEffect(() => {
        setSelectedIds(prev => {
            if (prev.length === 0) return prev;
            const visible = new Set(filteredEquipments.map(eq => eq.id));
            const next = prev.filter(id => visible.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [filteredEquipments]);

    const visibleIds = filteredEquipments.map(eq => eq.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
    const someVisibleSelected = selectedIds.length > 0 && !allVisibleSelected;

    const toggleRowSelection = (id: number) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };

    const toggleSelectAllVisible = () => {
        setSelectedIds(allVisibleSelected ? [] : visibleIds);
    };

    // Descripción de cada seleccionado para el modal de confirmación.
    const selectedDescriptions = filteredEquipments
        .filter(eq => selectedIds.includes(eq.id))
        .map(eq => [eq.internalCode, eq.vehicleNumber, eq.name].filter(Boolean).join(' - '));

    const handleConfirmBulkDelete = async () => {
        if (!onDeleteMultipleEquipments) return;
        setIsBulkDeleting(true);
        try {
            await onDeleteMultipleEquipments(selectedIds);
            setSelectedIds([]);
            setIsBulkDeleteModalOpen(false);
        } finally {
            setIsBulkDeleting(false);
        }
    };

    const [specialProject, setSpecialProject] = useState<number | null>(null);

    useEffect(() => {
        const fetchEmployeesAndProjects = async () => {
            const fetchedEmployees = await getEmployees();
            setEmployees(fetchedEmployees);
            const fetchedProjects = await getProjects();
            setProjects(fetchedProjects.filter((p: Project) => p.status === true || p.status === undefined));
        };
        fetchEmployeesAndProjects();
    }, []);

    useEffect(() => {
        const fetchCategoryEquipmentList = async () => {
            if (editableEquipment) {
                try {
                    const categories = await getCategoryEquipment();
                    setCategoryEquipmentList(categories);
                } catch (error) {
                    console.error('Error fetching category equipment list:', error);
                }
            }
        };
        fetchCategoryEquipmentList();
    }, [editableEquipment]);

    const uniqueValues = React.useMemo<string[]>(() => {
        if (!filterMenu.columnKey) return [];
        let values: (string | null | undefined)[] = [];
        const key = filterMenu.columnKey;
        switch (key) {
            case 'project':
                values = equipments.map(eq => getProjectName(eq));
                break;
            case 'costCenter':
                values = equipments.map(eq => getCostCenter(eq));
                break;
            case 'internalCode':
                values = equipments.map(eq => eq.internalCode);
                break;
            case 'vehicleNumber':
                values = equipments.map(eq => eq.vehicleNumber);
                break;
            case 'name':
                values = equipments.map(eq => eq.name);
                break;
            case 'owner':
                return ['Propio', 'Rentado'];
            case 'employee':
                values = equipments.map(eq =>
                    eq.employee ? `${eq.employee.name} ${eq.employee.firstName} ${eq.employee.lastName}` : 'No aplica'
                );
                break;
            case 'rentalCost':
                values = equipments.map(eq =>
                    new Intl.NumberFormat('es-CL', {
                        style: 'currency',
                        currency: 'CLP',
                        minimumFractionDigits: 0,
                    }).format(eq.rentalCost || 0)
                );
                break;
            case 'observations':
                values = equipments.map(eq => eq.observations);
                break;
        }
        return Array.from(new Set(values.filter((value): value is string => !!value))).sort();
    }, [equipments, filterMenu.columnKey]);

    const simplifiedProjects = projects.map(({ id, name }) => ({
        id: id as number,
        name: name as string,
    }));

    const handleEdit = (equipment: Equipment) => {
        setEditMode(equipment.id);
        setEditableEquipment({ ...equipment });
        setSelectedEmployee(equipment.employee || null);

        if (!equipment.project) {
            setSpecialProject(-1);
        } else if (equipment.project.id === ID_DESVINCULADO) {
            setSpecialProject(ID_DESVINCULADO);
        } else {
            setSpecialProject(null);
        }
    };

    const handleOwnerChange = (event: SelectChangeEvent<number>) => {
        if (editableEquipment) {
            setEditableEquipment({ ...editableEquipment, owner: Number(event.target.value) });
            setFormErrors((prev) => ({
                ...prev,
                project: false,
            }));
        }
    };

    const handleOpenConfirmModal = (id: number, name: any) => {
        setEquipmentId(id);
        setEquipmentName(name);
        setIsConfirmModalOpen(true)
    }

    const handleEmployeeChange = (newValue: Employee | null) => {
        setSelectedEmployee(newValue);
        setFormErrors((prev) => ({
            ...prev,
            employee: false,
        }));
        if (editableEquipment) {
            setEditableEquipment({ ...editableEquipment, employee: newValue });
        }
    };

    const handleSave = async (id: number) => {
        if (!editableEquipment) return;

        const ownerValue = Number(editableEquipment.owner);
        const isRentado = ownerValue === 0;

        const isProjectInvalid = isRentado && !editableEquipment.project &&
                               specialProject !== -1 && specialProject !== ID_DESVINCULADO;

        const errors = {
            project: isProjectInvalid,
            employee:
                !!editableEquipment.project &&
                editableEquipment.project.id !== -1 &&
                editableEquipment.project.id !== ID_DESVINCULADO &&
                selectedEmployee === null,
        };

        setFormErrors(errors);

        const hasErrors = Object.values(errors).some((error) => error);
        if (hasErrors) return;

        try {
            onEditEquipment({
                ...editableEquipment,
                owner: ownerValue,
                employee: selectedEmployee?.name === "Externo / No Aplica" ? null : selectedEmployee,
            });
            setEditMode(null);
        } catch (error) {
            console.error('Error updating equipment:', error);
        }
    };

    const handleDelete = async (id: number) => {
        onDeleteEquipment(equipmentId);
        setIsConfirmModalOpen(false);
    };

    const handleFilterMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
        columnKey: keyof typeof columnFilters
    ) => {
        setFilterMenu({ anchorEl: event.currentTarget, columnKey });
    };

    const handleFilterMenuClose = () => {
        setFilterMenu({ anchorEl: null, columnKey: null });
    };

    // Recibe la selección ya calculada por el menú de filtro
    const handleColumnFilterChange = (selected: string[]) => {
        if (!filterMenu.columnKey) return;
        setColumnFilters(prevFilters => ({
            ...prevFilters,
            [filterMenu.columnKey as string]: selected,
        }));
    };

    // Definición de Columnas (cabeceras alineadas al Excel de importación)
    const columnsDefinition = [
        { id: 'internalCode', label: 'Código interno' },
        { id: 'vehicleNumber', label: 'Patente' },
        { id: 'name', label: 'Equipo' },
        { id: 'owner', label: 'Propiedad' },
        { id: 'employee', label: 'Responsable' },
        { id: 'project', label: 'Proyecto' },
        { id: 'costCenter', label: 'Central de Coste' },
        { id: 'rentalCost', label: 'Costo arriendo' },
        { id: 'observations', label: 'Comentario' },
    ];

    // Columnas visibles según reportDate (oculta 'project' en histórico) y selección del usuario
    const isColumnVisible = (id: string) => {
        if (reportDate && id === 'project') return false;
        return visibleColumns[id] !== false;
    };

    return (
        <Grid2>
            <TextField
                label="Buscar por Proyecto, patente o equipo"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                fullWidth
                margin="normal"
            />

            {/* Barra de acciones de selección múltiple */}
            {allowMultiSelect && selectedIds.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1, p: 1.5, borderRadius: 1, backgroundColor: '#e3f2fd' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#004F86' }}>
                        {selectedIds.length === 1 ? '1 registro seleccionado' : `${selectedIds.length} registros seleccionados`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={() => setSelectedIds([])}>
                            Quitar selección
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => setIsBulkDeleteModalOpen(true)}
                        >
                            Eliminar seleccionados
                        </Button>
                    </Box>
                </Box>
            )}

            <TableContainer component={Paper} sx={{ minWidth: 650, maxHeight: 700, overflow: 'auto' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {allowMultiSelect && (
                                <TableCell padding="checkbox" sx={{ verticalAlign: 'top', p: 1 }}>
                                    <Checkbox
                                        checked={allVisibleSelected}
                                        indeterminate={someVisibleSelected}
                                        onChange={toggleSelectAllVisible}
                                        title="Seleccionar todo lo visible"
                                    />
                                </TableCell>
                            )}
                            {/* Se muestran solo las columnas visibles (histórico + selección del usuario) */}
                            {columnsDefinition
                                .filter(col => isColumnVisible(col.id))
                                .map((col) => (
                                <TableCell key={col.id} sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {col.label}
                                        <IconButton size="small" onClick={(e) => handleFilterMenuOpen(e, col.id as any)}
                                            // @ts-ignore
                                            sx={{ color: columnFilters[col.id]?.length > 0 ? '#004F86' : 'inherit' }}
                                        >
                                            <FilterListIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            ))}
                            <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        title="Mostrar/ocultar columnas"
                                        onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                                        sx={{ color: '#004F86' }}
                                    >
                                        <ViewColumnIcon fontSize="small" />
                                    </IconButton>
                                    Acción
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredEquipments.map((equipment) => (
                            <TableRow key={equipment.id} selected={selectedIds.includes(equipment.id)}>
                                {allowMultiSelect && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedIds.includes(equipment.id)}
                                            onChange={() => toggleRowSelection(equipment.id)}
                                            disabled={editMode === equipment.id}
                                        />
                                    </TableCell>
                                )}
                                {editMode === equipment.id ? (
                                    <>
                                        {/* --- MODO EDICIÓN --- */}
                                        {isColumnVisible('internalCode') && (
                                            <TableCell>
                                                <TextField value={editableEquipment?.internalCode || ''}
                                                    onChange={(e) => setEditableEquipment((prev) => ({ ...prev!, internalCode: e.target.value }))} />
                                            </TableCell>
                                        )}
                                        {isColumnVisible('vehicleNumber') && (
                                            <TableCell>
                                                <TextField value={editableEquipment?.vehicleNumber || ''}
                                                    onChange={(e) => setEditableEquipment((prev) => ({ ...prev!, vehicleNumber: e.target.value }))} />
                                            </TableCell>
                                        )}
                                        {isColumnVisible('name') && (
                                            <TableCell>
                                                <FormControl fullWidth>
                                                    <Select
                                                        value={categoryEquipmentList.some((category) => category.description === editableEquipment?.name) ? editableEquipment?.name : ''}
                                                        onChange={(e) => setEditableEquipment((prev) => ({ ...prev!, name: e.target.value }))}
                                                    >
                                                        <MenuItem value="">Seleccione un equipo</MenuItem>
                                                        {categoryEquipmentList.map((category) => (
                                                            <MenuItem key={category.id} value={category.description}>{category.description}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                        )}

                                        {isColumnVisible('owner') && (
                                            <TableCell>
                                                <Select
                                                    value={Number(editableEquipment?.owner) === 1 ? 1 : 0}
                                                    onChange={handleOwnerChange}
                                                    fullWidth
                                                >
                                                    <MenuItem value={0}>Rentado</MenuItem>
                                                    <MenuItem value={1}>Propio</MenuItem>
                                                </Select>
                                            </TableCell>
                                        )}

                                        {isColumnVisible('employee') && (
                                            <TableCell>
                                                <Autocomplete
                                                    options={[...employees, { id: -1, rut: '', name: 'Externo / No Aplica', firstName: '', lastName: '' }]}
                                                    getOptionLabel={(option) => option.name === 'Externo / No Aplica' ? option.name : `${option.rut} ${option.name} ${option.firstName} ${option.lastName}`}
                                                    value={selectedEmployee || { id: -1, name: 'Externo / No Aplica', rut: '', firstName: '', lastName: '' }}
                                                    onChange={(event, newValue) => handleEmployeeChange(newValue)}
                                                    autoHighlight
                                                    noOptionsText="Sin coincidencias"
                                                    renderInput={(params) => <TextField {...params} label="Responsable" placeholder="Buscar por RUT o nombre" error={formErrors.employee} />}
                                                    isOptionEqualToValue={(option, value) => option.id === value.id && option.name === value.name}
                                                />
                                            </TableCell>
                                        )}

                                        {isColumnVisible('project') && (
                                            <TableCell>
                                                <FormControl fullWidth variant="outlined" error={formErrors.project}>
                                                    <Select
                                                        value={editableEquipment?.project?.id ?? (specialProject !== null ? specialProject : '')}
                                                        onChange={(event) => {
                                                            const projectId = Number(event.target.value);
                                                            if (projectId === -1) {
                                                                setEditableEquipment((prev) => ({ ...prev!, project: null }));
                                                                setSpecialProject(projectId);
                                                            }
                                                            else if (projectId === ID_DESVINCULADO) {
                                                                setEditableEquipment((prev) => ({
                                                                    ...prev!,
                                                                    project: { id: ID_DESVINCULADO, name: 'Desvinculado', status: true } as Project,
                                                                }));
                                                                setSpecialProject(ID_DESVINCULADO);
                                                            }
                                                            else {
                                                                const projectSelected = simplifiedProjects.find((p) => p.id === projectId);
                                                                setEditableEquipment((prev) => ({
                                                                    ...prev!,
                                                                    project: projectSelected ? { ...projectSelected, status: true } : null,
                                                                }));
                                                                setSpecialProject(null);
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value={-1}>Mantenimiento / Faena</MenuItem>
                                                        <MenuItem value={ID_DESVINCULADO}>Desvinculado</MenuItem>
                                                        {simplifiedProjects.filter(p => p.id !== ID_DESVINCULADO).map((project) => (
                                                            <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                        )}

                                        {/* Central de Coste: solo lectura, se asigna automáticamente por el proyecto */}
                                        {isColumnVisible('costCenter') && (
                                            <TableCell>{getCostCenter(editableEquipment || equipment)}</TableCell>
                                        )}
                                        {isColumnVisible('rentalCost') && (
                                            <TableCell>
                                                <TextField
                                                    value={editableEquipment?.rentalCost?.toString() || ''}
                                                    onChange={(e) => setEditableEquipment((prev) => ({ ...prev!, rentalCost: Number(e.target.value) }))}
                                                    InputProps={{ startAdornment: <Typography variant="body2">CLP </Typography> }}
                                                />
                                            </TableCell>
                                        )}
                                        {isColumnVisible('observations') && (
                                            <TableCell>
                                                <TextField value={editableEquipment?.observations || ''}
                                                    onChange={(e) => setEditableEquipment((prev) => ({ ...prev!, observations: e.target.value }))} />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <IconButton color="primary" onClick={() => handleSave(equipment.id)}><Save /></IconButton>
                                            <Button onClick={() => setEditMode(null)}>Cancelar</Button>
                                        </TableCell>
                                    </>
                                ) : (
                                    <>
                                        {/* --- MODO VISUALIZACIÓN --- */}
                                        {isColumnVisible('internalCode') && <TableCell>{equipment.internalCode}</TableCell>}
                                        {isColumnVisible('vehicleNumber') && <TableCell>{equipment.vehicleNumber}</TableCell>}
                                        {isColumnVisible('name') && <TableCell>{equipment.name}</TableCell>}
                                        {isColumnVisible('owner') && <TableCell>{Number(equipment.owner) === 1 ? 'Propio' : 'Rentado'}</TableCell>}
                                        {isColumnVisible('employee') && (
                                            <TableCell>
                                                {equipment.employee
                                                    ? `${equipment.employee.name} ${equipment.employee.firstName} ${equipment.employee.lastName}`
                                                    : 'No aplica'}
                                            </TableCell>
                                        )}
                                        {isColumnVisible('project') && <TableCell>{getProjectName(equipment)}</TableCell>}
                                        {isColumnVisible('costCenter') && <TableCell>{getCostCenter(equipment)}</TableCell>}
                                        {isColumnVisible('rentalCost') && (
                                            <TableCell>
                                                {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(equipment.rentalCost || 0)}
                                            </TableCell>
                                        )}
                                        {isColumnVisible('observations') && <TableCell>{equipment.observations}</TableCell>}
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleEdit(equipment)}
                                                disabled={!!reportDate} // Bloquear edición si es histórico
                                                sx={{ color: reportDate ? 'gray' : 'primary.main' }}
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                color="primary"
                                                onClick={() => {
                                                    if (reportDate) {
                                                        onDeleteEquipment(equipment.id); // Borrar directo si es histórico
                                                    } else {
                                                        handleOpenConfirmModal(equipment.id, equipment.name);
                                                    }
                                                }}
                                                title={reportDate ? "Quitar del reporte" : "Eliminar"}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <ColumnFilterMenu
                anchorEl={filterMenu.anchorEl}
                values={uniqueValues}
                selected={filterMenu.columnKey ? columnFilters[filterMenu.columnKey] : []}
                onClose={handleFilterMenuClose}
                onChange={handleColumnFilterChange}
            />

            {/* Menú para mostrar/ocultar columnas */}
            <Menu
                anchorEl={columnsMenuAnchor}
                open={Boolean(columnsMenuAnchor)}
                onClose={() => setColumnsMenuAnchor(null)}
                PaperProps={{ style: { maxHeight: 400 } }}
            >
                {columnsDefinition
                    .filter(col => !(reportDate && col.id === 'project'))
                    .map((col) => (
                    <MenuItem key={col.id} onClick={() => toggleColumn(col.id)}>
                        <FormControlLabel
                            control={<Checkbox checked={visibleColumns[col.id] !== false} />}
                            label={col.label}
                            sx={{ pointerEvents: 'none' }}
                        />
                    </MenuItem>
                ))}
            </Menu>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="contained" color="primary" onClick={onDownloadFormat}>Descargar Formato</Button>
                <input accept=".xlsx, .xls" style={{ display: 'none' }} id="import-file" type="file" onChange={onImportEquipments} />
                <label htmlFor="import-file">
                    <Button variant="contained" color="primary" component="span">Importar Equipos</Button>
                </label>
                <Button variant="contained" color="primary" onClick={onExportEquipments}>Exportar Equipos</Button>
                {loading && <p>Cargando...</p>}
            </Box>
            <ConfirmDeleteModal open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}
                onSubmit={handleDelete} id={equipmentId} name={equipmentName} title={'EQUIPMENT'} />

            <ConfirmBulkDeleteModal
                open={isBulkDeleteModalOpen}
                onClose={() => setIsBulkDeleteModalOpen(false)}
                onConfirm={handleConfirmBulkDelete}
                items={selectedDescriptions}
                entityLabel="equipo"
                entityLabelPlural="equipos"
                loading={isBulkDeleting}
            />
        </Grid2>
    );
};

export default EquipmentTable;